/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css';
/* Basic CSS for apps built with Ionic */
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';
/* Optional CSS utils that can be commented out */
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/float-elements.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/text-transformation.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/display.css';
/* Theme variables & global css. dark.css must come before variables.css so a
   brand's own dark palette overrides the shared dark neutrals. */
import './theme/dark.css';
import './theme/variables.css';
import './theme/global.css';
import './theme/flexbox.css';
import './theme/ios.css';
import './theme/md.css';
import './theme/primevue.css';
/* Swiper css */
import 'swiper/css';
import 'swiper/css/pagination';

import { App as CapacitorApp, URLOpenListenerEvent } from '@capacitor/app';
import { IonicVue, useBackButton } from '@ionic/vue';
import {
  displayExitAppAlert,
  displaySignOutAlert,
} from '@/services/alertService';
import { getSessionFromCode, setupAuth } from './services/auth';
import { initialisePinia, useStore } from '@/store';

import App from './App.vue';
import Aura from '@primevue/themes/aura';
import BaseLayout from './components/BaseLayout.vue';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import PrimeVue from 'primevue/config';
import { SplashScreen } from '@capacitor/splash-screen';
import Tooltip from 'primevue/tooltip';
import VueQrcode from '@chenfengyuan/vue-qrcode';
import config from './config';
import { createApp } from 'vue';
import { i18n } from './utils/i18n';
import logger from '@/services/logger';
import { errorDetails } from '@/services/logger/errorDetails';
import { setLocale } from '@/utils/locale';
import router from './router';
import { setupLoggerDb } from './services/logger/store';
import { reportDeviceIntegrity } from './services/deviceIntegrity';
import { showErrorToast } from './utils/toast';
import { startServices } from './services/serviceControl';
import { initTheme, setThemePreference } from './services/theme';

logger.info('NODE_ENV', import.meta.env.MODE);

// Before anything renders, so the first paint is already in the right palette
initTheme();

await setupAuth();

const pinia = initialisePinia();

const app = createApp(App)
  .use(IonicVue)
  .use(logger)
  .use(pinia)
  .use(i18n)
  .use(router)
  .use(PrimeVue, {
    theme: {
      preset: Aura,
      options: {
        darkModeSelector: '.ion-palette-dark',
      },
    },
  });

const isNative = Capacitor.isNativePlatform();

// Add tooltips if we're not on a native device
app.directive('tooltip', !isNative ? Tooltip : {});
app.component('base-layout', BaseLayout);
app.component('vue-qr-code', VueQrcode);

// Fired when the app comes back to the foreground
const onResume = async () => {
  await useStore().$hydrate();
  logger.info('onResume');

  await setupLoggerDb();
  await startServices('resume');
};

// Fired when the app goes to the background
const onPause = async () => {
  logger.info('onPause');
  await useStore().$persist();
};

useBackButton(10, async () => {
  const route = router.currentRoute.value.path;
  if (route === '/signin') {
    await displayExitAppAlert();
  } else if (route === '/home') {
    await displaySignOutAlert();
  } else {
    router.back();
  }
});

// Fired after app is first mounted
const onReady = async () => {
  const store = useStore();
  await store.$hydrate();
  store.activeBooking.bookingType = undefined;

  // Set locale and theme from store
  store.locale = setLocale(store.locale);
  store.themePreference = setThemePreference(store.themePreference);

  logger.info('onReady', { appVersion: config.app.version });
  await setupLoggerDb();

  if (isNative) {
    await CapacitorApp.addListener('resume', onResume);
    await CapacitorApp.addListener('pause', onPause);
  }

  try {
    await startServices('ready');
  } catch (error: any) {
    logger.error('initaliseServices', errorDetails(error));
  } finally {
    await SplashScreen.hide({ fadeOutDuration: 500 });
  }

  await reportDeviceIntegrity();
};

document.addEventListener('visibilitychange', async () => {
  if (isNative) return;
  const visibility = document.visibilityState;
  logger.info(`visibilitychange - ${visibility}`);
  if (visibility === 'hidden') {
    await onPause();
  }

  if (visibility === 'visible') {
    await onResume();
  }
});

window.onunhandledrejection = (event) => {
  logger.error('Unhandled rejection', errorDetails(event.reason));
};

// listen for App Callback Urls
if (config.services.aws.awsConfig.Auth?.Cognito.loginWith && isNative) {
  CapacitorApp.addListener(
    'appUrlOpen',
    async (event: URLOpenListenerEvent) => {
      logger.debug('appUrlOpen', event);
      try {
        await Browser.close();
        // Example url: capacitor://com.catalinasoftware.freedom.gox/sso?code=****&state=****
        const url = event.url;
        if (url && url.startsWith('capacitor://')) {
          const error = new URL(event.url)?.searchParams.get(
            'error_description',
          );
          const code = new URL(event.url)?.searchParams.get('code');

          if (error) {
            throw new Error(decodeURIComponent(error));
          } else if (code) {
            await getSessionFromCode(code);
            router.push({ name: 'sso' });
          }
        }
      } catch (e: any) {
        logger.error('appUrlOpen', e.message ?? e);
        await showErrorToast();
      }
    },
  );
}

// Google Tag Manager
const { gtmId } = config.app;

if (gtmId) {
  // Inject GTM script
  const script = document.createElement('script');
  script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','goxDataLayer','${gtmId}');`;
  document.head.prepend(script);

  // Inject noscript fallback
  const noscript = document.createElement('noscript');
  noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
  document.body.prepend(noscript);
}

router
  .isReady()
  .then(() => {
    app.mount('#app');
  })
  .then(async () => {
    onReady();
  });
